import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Handshake,
  Download,
  CheckCircle2,
  Building2,
  GraduationCap,
  Users,
  Scale,
  Sparkles,
  Target,
  ArrowRight,
  Mail,
  Phone,
} from "lucide-react";
import {
  submitFoundingPartnerInterest,
  requestPartnerUploadUrl,
} from "@/lib/api/founding-partners.functions";
import { useServerFn } from "@tanstack/react-start";
import { site } from "@/lib/site";

const PACK_URL = "/downloads/SA-LexPractiq-Founding-Partner-Pack.pptx";

export const Route = createFileRoute("/founding-partners")({
  head: () => ({
    meta: [
      { title: "Founding Partners — SA LexPractiq | Shape SA's Legal Talent Pipeline" },
      {
        name: "description",
        content:
          "Join SA LexPractiq as a Founding Partner. Download the partner pack and help build South Africa's leading Legal Experience Platform for students, graduates and firms.",
      },
      { property: "og:title", content: "Founding Partners — SA LexPractiq" },
      {
        property: "og:description",
        content:
          "Law firms, universities and corporates: partner with us to train, verify and hire practice-ready SA legal talent.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: FoundingPartnersPage,
});

const PARTNER_TYPES = [
  { value: "law_firm", label: "Law firm / chambers" },
  { value: "university", label: "University / law school" },
  { value: "corporate", label: "Corporate legal department" },
  { value: "recruiter", label: "Recruiter / talent agency" },
  { value: "mentor", label: "Individual mentor / practitioner" },
  { value: "other", label: "Other" },
] as const;

const INTEREST_OPTIONS = [
  "Sponsor simulations",
  "Recruit graduates",
  "Offer mentorship / Q&A",
  "Assign to students",
  "Co-brand content",
  "Fund scholarships",
  "Research collaboration",
];

const PARTNERSHIP_TIERS = [
  {
    value: "founding",
    label: "Founding Partner",
    blurb: "Logo on platform + shape the roadmap. Limited to 10.",
  },
  {
    value: "supporter",
    label: "Supporter",
    blurb: "Sponsor one simulation or scholarship cohort.",
  },
  {
    value: "mentor",
    label: "Mentor",
    blurb: "Give 30–60 min/month reviewing submissions.",
  },
  { value: "exploring", label: "Just exploring", blurb: "Have a chat first." },
] as const;


function FoundingPartnersPage() {
  return (
    <main style={{ background: "#f8fafc", minHeight: "100vh" }}>
      <Hero />
      <ProblemBenefits />
      <Participation />
      <Roadmap />
      <PartnerForm />
      <ContactStrip />
    </main>
  );
}

function Hero() {
  return (
    <section
      style={{
        background:
          "linear-gradient(135deg, #0B1F3A 0%, #14315a 55%, #0f2a4c 100%)",
        color: "#fff",
        padding: "5rem 1.25rem 4rem",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 14px",
            borderRadius: 999,
            background: "rgba(212,175,55,0.15)",
            color: "#f5d572",
            fontSize: "0.75rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: "1.5rem",
            border: "1px solid rgba(212,175,55,0.35)",
          }}
        >
          <Sparkles size={12} /> Founding Partner Programme · 2026 Cohort
        </span>
        <h1
          style={{
            fontSize: "clamp(2.2rem, 5vw, 3.6rem)",
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            margin: "0 0 1.25rem",
            maxWidth: 900,
          }}
        >
          Help build South Africa's{" "}
          <span style={{ color: "#f5d572" }}>Legal Experience Platform</span>
        </h1>
        <p
          style={{
            fontSize: "1.15rem",
            lineHeight: 1.6,
            maxWidth: 720,
            color: "rgba(255,255,255,0.85)",
            margin: "0 0 2rem",
          }}
        >
          SA LexPractiq is where law students prove they can actually do the work — drafting,
          researching, arguing and advising on real South African matters. As a Founding Partner
          you shape the platform, get first pick of verified talent, and put your name on the
          country's first practice-first legal ecosystem.
        </p>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          <a
            href={PACK_URL}
            download
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "#f5d572",
              color: "#0B1F3A",
              padding: "0.85rem 1.4rem",
              borderRadius: 10,
              fontWeight: 700,
              textDecoration: "none",
              fontSize: "0.95rem",
            }}
          >
            <Download size={16} /> Download partner pack (PPTX)
          </a>
          <a
            href="#partner-form"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "transparent",
              color: "#fff",
              padding: "0.85rem 1.4rem",
              borderRadius: 10,
              fontWeight: 600,
              textDecoration: "none",
              fontSize: "0.95rem",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            Register interest <ArrowRight size={16} />
          </a>
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "2rem",
            marginTop: "2.5rem",
            fontSize: "0.85rem",
            color: "rgba(255,255,255,0.75)",
          }}
        >
          <span>✓ 38 learners already registered</span>
          <span>✓ 10+ authentic SA simulations live</span>
          <span>✓ Verified portfolios &amp; certificates</span>
        </div>
      </div>
    </section>
  );
}

function ProblemBenefits() {
  return (
    <section style={{ maxWidth: 1100, margin: "0 auto", padding: "4rem 1.25rem" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "2rem" }}>
        <div>
          <span
            style={{
              display: "inline-block",
              fontSize: "0.7rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#0B1F3A",
              fontWeight: 700,
              marginBottom: "0.5rem",
            }}
          >
            The problem
          </span>
          <h2 style={{ fontSize: "1.8rem", margin: "0 0 1rem", color: "#0B1F3A", letterSpacing: "-0.01em" }}>
            SA graduates leave law school without ever doing the work
          </h2>
          <p style={{ color: "#475569", lineHeight: 1.7, fontSize: "1rem", margin: 0 }}>
            Most LLB graduates arrive at their first interview never having drafted a letter of
            demand, read a full judgment, prepared a CCMA referral, or delivered a legal opinion.
            Firms spend the first 6–12 months of articles teaching what university couldn't. The
            profession loses time, talent and diversity as a result.
          </p>
        </div>
        <div>
          <span
            style={{
              display: "inline-block",
              fontSize: "0.7rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#0B1F3A",
              fontWeight: 700,
              marginBottom: "0.5rem",
            }}
          >
            The benefit to you
          </span>
          <h2 style={{ fontSize: "1.8rem", margin: "0 0 1rem", color: "#0B1F3A", letterSpacing: "-0.01em" }}>
            Recruit on proven skill — not just a CV
          </h2>
          <ul style={{ color: "#475569", lineHeight: 1.75, fontSize: "1rem", margin: 0, paddingLeft: "1.1rem" }}>
            <li>Review verified portfolios before you interview.</li>
            <li>Assign your own custom scenarios to shortlisted candidates.</li>
            <li>Co-brand simulations aligned to your firm's practice areas.</li>
            <li>Reach 100% of SA law faculties, not just the top three.</li>
            <li>Founding Partner logo on the platform, deck and certificates.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function Participation() {
  const tiers = [
    {
      icon: <Building2 size={22} />,
      name: "Law firms & chambers",
      body: "Sponsor a simulation, review candidate portfolios, invite top performers to articles or vacation programmes, and host a live mentor Q&A.",
    },
    {
      icon: <GraduationCap size={22} />,
      name: "Universities & law schools",
      body: "Assign LexPractiq simulations as coursework or vacation practical, track cohort progress, and standardise practical assessment across classes.",
    },
    {
      icon: <Users size={22} />,
      name: "Corporate legal departments",
      body: "Get first access to compliance-ready graduates and paralegals; run POPIA, commercial and labour scenarios modelled on your risk profile.",
    },
    {
      icon: <Scale size={22} />,
      name: "Individual mentors",
      body: "Give 30–60 minutes a month reviewing submissions and running Q&A sessions — appear on the mentor network and shape the next generation.",
    },
  ];
  return (
    <section
      style={{
        background: "#0B1F3A",
        color: "#fff",
        padding: "4rem 1.25rem",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <span
            style={{
              display: "inline-block",
              fontSize: "0.7rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#f5d572",
              fontWeight: 700,
              marginBottom: "0.75rem",
            }}
          >
            How you can participate
          </span>
          <h2 style={{ fontSize: "2rem", margin: 0, letterSpacing: "-0.01em" }}>
            Four ways to partner with SA LexPractiq
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem" }}>
          {tiers.map((t) => (
            <div
              key={t.name}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 14,
                padding: "1.5rem",
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: "rgba(212,175,55,0.15)",
                  color: "#f5d572",
                  display: "grid",
                  placeItems: "center",
                  marginBottom: "0.85rem",
                }}
              >
                {t.icon}
              </div>
              <h3 style={{ margin: "0 0 0.5rem", fontSize: "1.05rem" }}>{t.name}</h3>
              <p style={{ margin: 0, fontSize: "0.9rem", lineHeight: 1.6, color: "rgba(255,255,255,0.75)" }}>{t.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Roadmap() {
  const items = [
    { q: "Q3 2026", t: "Founding Partners cohort", d: "Onboard 5 firms, 3 universities and 15 mentors. Publish 25 simulations." },
    { q: "Q4 2026", t: "Verified portfolios at scale", d: "1 000 verified candidate portfolios; recruiter dashboard v2 with custom assessments." },
    { q: "Q1 2027", t: "University partnerships", d: "Formal MOUs with SA law faculties to accredit LexPractiq work toward practical marks." },
    { q: "Q2 2027", t: "Articles pipeline", d: "First formal articles offers made via LexPractiq shortlists; mentor network hits 50 practitioners." },
  ];
  return (
    <section style={{ maxWidth: 1100, margin: "0 auto", padding: "4rem 1.25rem" }}>
      <div style={{ marginBottom: "2rem" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: "0.7rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#0B1F3A",
            fontWeight: 700,
            marginBottom: "0.5rem",
          }}
        >
          <Target size={12} /> Roadmap
        </span>
        <h2 style={{ fontSize: "2rem", margin: 0, color: "#0B1F3A", letterSpacing: "-0.01em" }}>
          Where we're going next
        </h2>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
        {items.map((i) => (
          <div
            key={i.q}
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: "1.25rem",
              borderLeft: "3px solid #f5d572",
            }}
          >
            <div style={{ fontSize: "0.75rem", color: "#0B1F3A", fontWeight: 700, marginBottom: "0.4rem" }}>{i.q}</div>
            <h3 style={{ margin: "0 0 0.35rem", fontSize: "1rem", color: "#0B1F3A" }}>{i.t}</h3>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b", lineHeight: 1.55 }}>{i.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

type UploadedAttachment = {
  path: string;
  name: string;
  size: number;
  contentType: string | null;
};

const ALLOWED_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
  "image/webp",
];
const MAX_BYTES = 20 * 1024 * 1024;

function PartnerForm() {
  const [interests, setInterests] = useState<string[]>([]);
  const [tier, setTier] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const getUploadUrl = useServerFn(requestPartnerUploadUrl);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      submitFoundingPartnerInterest({ data: payload as never }),
    onSuccess: () => setSubmitted(true),
  });

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;
    if (attachments.length + files.length > 5) {
      setUploadError("You can attach up to 5 files.");
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > MAX_BYTES) throw new Error(`${f.name} is larger than 20 MB.`);
        if (!ALLOWED_MIME.includes(f.type)) throw new Error(`${f.name}: file type not allowed.`);
        const meta = await getUploadUrl({
          data: { filename: f.name, size: f.size, contentType: f.type } as never,
        });
        const put = await fetch(meta.signedUrl, {
          method: "PUT",
          headers: { "content-type": f.type },
          body: f,
        });
        if (!put.ok) throw new Error(`Upload failed for ${f.name}`);
        setAttachments((prev) => [
          ...prev,
          { path: meta.path, name: f.name, size: f.size, contentType: f.type },
        ]);
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function removeAttachment(path: string) {
    setAttachments((prev) => prev.filter((a) => a.path !== path));
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    mutation.mutate({
      organisation: String(fd.get("organisation") ?? ""),
      contact_name: String(fd.get("contact_name") ?? ""),
      role: String(fd.get("role") ?? "") || null,
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? "") || null,
      partner_type: String(fd.get("partner_type") ?? "other") as never,
      partnership_tier: tier || null,
      team_size: String(fd.get("team_size") ?? "") || null,
      website: String(fd.get("website") ?? "") || null,
      country: String(fd.get("country") ?? "") || null,
      city: String(fd.get("city") ?? "") || null,
      annual_hiring_target: String(fd.get("annual_hiring_target") ?? "") || null,
      interests,
      message: String(fd.get("message") ?? "") || null,
      attachments,
    } as never);
  }

  function toggleInterest(v: string) {
    setInterests((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  }



  return (
    <section
      id="partner-form"
      style={{
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
        padding: "4rem 1.25rem",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <Handshake size={36} style={{ color: "#0B1F3A", marginBottom: "0.75rem" }} />
          <h2 style={{ fontSize: "1.9rem", margin: "0 0 0.5rem", color: "#0B1F3A", letterSpacing: "-0.01em" }}>
            Become a Founding Partner
          </h2>
          <p style={{ color: "#475569", margin: 0, fontSize: "0.95rem" }}>
            Tell us a little about your organisation. We reply within two working days with a call
            invite and your customised partner brief.
          </p>
        </div>

        {submitted ? (
          <div
            style={{
              background: "#fff",
              border: "1px solid #d1fae5",
              borderRadius: 14,
              padding: "2rem",
              textAlign: "center",
            }}
          >
            <CheckCircle2 size={40} style={{ color: "#059669", marginBottom: "0.75rem" }} />
            <h3 style={{ margin: "0 0 0.5rem", color: "#0B1F3A" }}>Thank you — we've got it.</h3>
            <p style={{ margin: 0, color: "#475569", fontSize: "0.95rem" }}>
              Danny will personally reach out within 2 working days. In the meantime, feel free to
              download the partner pack above.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: "2rem",
              display: "grid",
              gap: "1rem",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <Field label="Organisation *" name="organisation" required />
              <Field label="Contact name *" name="contact_name" required />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <Field label="Role / title" name="role" placeholder="e.g. Managing Partner" />
              <Field label="Website" name="website" placeholder="e.g. yourfirm.co.za" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem" }}>
              <Field label="Country" name="country" placeholder="South Africa" />
              <Field label="City" name="city" placeholder="Johannesburg" />
              <Field label="Team size" name="team_size" placeholder="e.g. 5–20" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <Field label="Email *" name="email" type="email" required />
              <Field label="Phone" name="phone" type="tel" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0B1F3A" }}>
                  Partner type *
                </span>
                <select name="partner_type" required defaultValue="" style={inputStyle}>
                  <option value="" disabled>
                    Choose one…
                  </option>
                  {PARTNER_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <Field
                label="Annual hiring / intake target"
                name="annual_hiring_target"
                placeholder="e.g. 3 CAs per year"
              />
            </div>
            <div>
              <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0B1F3A", marginBottom: 8 }}>
                Partnership tier
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
                {PARTNERSHIP_TIERS.map((t) => {
                  const on = tier === t.value;
                  return (
                    <button
                      type="button"
                      key={t.value}
                      onClick={() => setTier(on ? "" : t.value)}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: `1px solid ${on ? "#0B1F3A" : "#cbd5e1"}`,
                        background: on ? "#0B1F3A" : "#fff",
                        color: on ? "#f5d572" : "#0B1F3A",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>{t.label}</div>
                      <div style={{ fontSize: "0.72rem", opacity: 0.85, marginTop: 3 }}>{t.blurb}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0B1F3A", marginBottom: 8 }}>
                What are you interested in? (select any)
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {INTEREST_OPTIONS.map((opt) => {
                  const on = interests.includes(opt);
                  return (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => toggleInterest(opt)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 999,
                        border: `1px solid ${on ? "#0B1F3A" : "#cbd5e1"}`,
                        background: on ? "#0B1F3A" : "#fff",
                        color: on ? "#f5d572" : "#334155",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0B1F3A" }}>
                Anything else you'd like us to know?
              </span>
              <textarea
                name="message"
                rows={4}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
              />
            </label>
            <div style={{ display: "grid", gap: 8 }}>
              <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0B1F3A" }}>
                Onboarding documents or company profile (optional)
              </span>
              <p style={{ fontSize: "0.78rem", color: "#475569", margin: 0 }}>
                PDF, Word, Excel or images. Max 5 files, 20 MB each. Stored privately —
                only visible to our admin team.
              </p>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                onChange={handleFileChange}
                disabled={uploading || attachments.length >= 5}
                style={{ fontSize: "0.85rem" }}
              />
              {uploading ? (
                <div style={{ fontSize: "0.8rem", color: "#0B1F3A" }}>Uploading…</div>
              ) : null}
              {uploadError ? (
                <div style={{ color: "#b91c1c", fontSize: "0.8rem" }}>{uploadError}</div>
              ) : null}
              {attachments.length > 0 ? (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 4 }}>
                  {attachments.map((a) => (
                    <li
                      key={a.path}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        padding: "0.4rem 0.6rem",
                        fontSize: "0.82rem",
                      }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {a.name} · {(a.size / 1024).toFixed(0)} KB
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.path)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#b91c1c",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div style={{ display: "none" }} aria-hidden>
              {/* marker to keep the closing label below stable */}
            </div>
            {/* eslint-disable-next-line */}
            {mutation.isError ? (
              <div style={{ color: "#b91c1c", fontSize: "0.85rem" }}>
                {(mutation.error as Error)?.message ?? "Submission failed."}
              </div>
            ) : null}
            <button
              type="submit"
              disabled={mutation.isPending}
              style={{
                background: "#0B1F3A",
                color: "#f5d572",
                border: "none",
                padding: "0.95rem 1.4rem",
                borderRadius: 10,
                fontWeight: 700,
                fontSize: "0.95rem",
                cursor: mutation.isPending ? "wait" : "pointer",
                marginTop: "0.5rem",
              }}
            >
              {mutation.isPending ? "Submitting…" : "Send my interest"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "0.7rem 0.85rem",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  fontSize: "0.9rem",
  background: "#fff",
  color: "#0f172a",
  width: "100%",
};

function Field({
  label,
  name,
  type = "text",
  required = false,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0B1F3A" }}>{label}</span>
      <input name={name} type={type} required={required} placeholder={placeholder} style={inputStyle} />
    </label>
  );
}

function ContactStrip() {
  return (
    <section
      style={{
        background: "#0B1F3A",
        color: "#fff",
        padding: "2.5rem 1.25rem",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <p style={{ margin: "0 0 0.5rem", fontSize: "0.85rem", color: "#f5d572", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Direct line to the founder
        </p>
        <h3 style={{ margin: "0 0 1rem", fontSize: "1.3rem" }}>Danny Nkadimeng — Founder &amp; Director</h3>
        <div style={{ display: "flex", justifyContent: "center", gap: "1.5rem", flexWrap: "wrap", fontSize: "0.95rem" }}>
          <a href={`mailto:${site.email}`} style={{ color: "#fff", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Mail size={16} /> {site.email}
          </a>
          <a href={`tel:${site.phoneIntl}`} style={{ color: "#fff", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Phone size={16} /> {site.phone}
          </a>
        </div>
      </div>
    </section>
  );
}
